import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormModule } from '@app/shared/components/form/form.module';
import { ToastService } from '@teorema/patron';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TextareaModule } from 'primeng/textarea';
import { Documentation } from 'src/app/models';
import { DocumentationService } from 'src/app/shared/services/documentation.service';
import { PathSelectorComponent } from '../../components/path-selector/path-selector.component';
import { PathAssignment } from '../../models/documentation-path.model';

@Component({
  selector: 'teo-wiki-documentation-form',
  templateUrl: './documentation-form.component.html',
  styleUrls: ['./documentation-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    SkeletonModule,
    TextareaModule,
    TooltipModule,
    PathSelectorComponent,
  ],
})
export class DocumentationFormComponent implements OnDestroy {
  loading = signal(false);
  editorLoading = signal(false);
  dialogMaximized = signal(false);
  modalVisible = false;

  entity: Documentation = new Documentation();
  entityId?: string;
  formGroup: FormGroup;

  editorUrl: SafeResourceUrl | null = null;
  editorOpen = false;
  private messageListener: ((e: MessageEvent) => void) | null = null;
  private titleSlugSub?: any;

  selectedPaths = signal<PathAssignment[]>([]);
  primaryPathId = signal<string | null>(null);

  @Input() relatedTicket?: any;
  @Output() onSave = new EventEmitter<Documentation>();

  private service = inject(DocumentationService);
  private formBuilder = inject(FormBuilder);
  private toastService = inject(ToastService);
  private sanitizer = inject(DomSanitizer);

  readonly categoryOptions = [
    { value: 'technical', label: 'Técnica' },
    { value: 'user_manual', label: 'Manual do Usuário' },
    { value: 'architecture', label: 'Arquitetura' },
    { value: 'process', label: 'Processo' },
    { value: 'integration', label: 'Integração' },
  ];

  readonly visibilityOptions = [
    { value: 'public', label: 'Pública' },
    { value: 'private', label: 'Privada' },
    { value: 'internal', label: 'Interna' },
  ];

  get safeEditorUrl(): SafeResourceUrl {
    return this.editorUrl as SafeResourceUrl;
  }

  constructor() {
    this.formGroup = this.formBuilder.group({
      title: [null, Validators.required],
      slug: [null],
      description: [null],
      content: [''],
      category: ['technical', Validators.required],
      visibility: ['internal', Validators.required],
      ai_context: [false],
      ai_priority: [5],
      ai_tags: [null],
      version: ['1.0.0', Validators.required],
      active: [1],
    });

    this.titleSlugSub = this.formGroup.get('title')?.valueChanges.subscribe(title => {
      if (!this.entity?.id || !this.formGroup.get('slug')?.value) {
        this.formGroup.patchValue(
          { slug: this.generateSlugLocal(title ?? '') },
          { emitEvent: false }
        );
      }
    });
  }

  async openModal(id?: string, ticket?: any): Promise<void> {
    this.entity = new Documentation();
    this.entityId = id;
    this.editorUrl = null;
    this.editorOpen = false;
    this.removeMessageListener();

    await this.find();
    this.updateForm();

    if (!this.entity.tickets) this.entity.tickets = [];
    if (!this.entity.systems) this.entity.systems = [];
    if (!this.entity.bw_projects) this.entity.bw_projects = [];

    if (!id && ticket) {
      this.entity.tickets = [ticket];
    }

    this.modalVisible = true;
  }

  cancelForm(): void {
    this.modalVisible = false;
  }

  onDialogHide(): void {
    this.editorUrl = null;
    this.editorOpen = false;
    this.dialogMaximized.set(false);
    this.removeMessageListener();
  }

  handleDialogMaximize(event: { maximized: boolean }): void {
    this.dialogMaximized.set(!!event?.maximized);
  }

  toggleDialogMaximize(): void {
    const btn = document.querySelector(
      '.documentation-dialog .p-dialog-maximize-button'
    ) as HTMLButtonElement | null;
    btn?.click();
  }

  async find(): Promise<void> {
    if (!this.entityId) return;

    this.loading.set(true);
    await this.service
      .findById(this.entityId)
      .then((response: any) => {
        if (response) {
          this.entity = response.data || response;
        }
      })
      .catch(() => {
        this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar os dados' });
      })
      .finally(() => {
        this.loading.set(false);
      });
  }

  updateForm(): void {
    if (!this.entity.id) {
      this.formGroup.reset();
      this.selectedPaths.set([]);
      this.primaryPathId.set(null);
      this.formGroup.patchValue({
        category: 'technical',
        visibility: 'internal',
        version: '1.0.0',
        ai_context: false,
        ai_priority: 5,
        active: 1,
      });
      return;
    }

    this.formGroup.patchValue(this.entity);

    const entityPaths = (this.entity as any).paths;
    if (entityPaths?.length > 0) {
      const mappedAssignments = entityPaths.map((p: any) => ({
        pathId: p.pivot?.path_id || p.id,
        isPrimary: p.pivot?.is_primary === 1 || p.pivot?.is_primary === true,
        position: p.pivot?.position || 0,
        fullPath: p.materialized_path || p.name,
      }));
      this.selectedPaths.set(mappedAssignments);
      const primary = mappedAssignments.find((a: any) => a.isPrimary);
      this.primaryPathId.set(primary?.pathId ?? mappedAssignments[0]?.pathId ?? null);
    } else {
      this.selectedPaths.set([]);
      this.primaryPathId.set(null);
    }
  }

  onPathsChange(assignments: PathAssignment[]): void {
    this.selectedPaths.set(assignments);
  }

  onPrimaryChange(primaryId: string): void {
    this.primaryPathId.set(primaryId);
  }

  async generateSlug(): Promise<void> {
    const title = this.formGroup.get('title')?.value;
    if (!title) {
      this.toastService.show({ color: 'warn', message: 'Digite um título primeiro' });
      return;
    }
    try {
      const response = await this.service.generateSlug(title);
      if (response.slug) {
        this.formGroup.patchValue({ slug: response.slug });
        this.toastService.show({ color: 'success', message: 'Slug gerado com sucesso!' });
      }
    } catch {
      this.toastService.show({ color: 'error', message: 'Erro ao gerar slug' });
    }
  }

  async openEditor(): Promise<void> {
    this.editorUrl = null;
    this.editorOpen = false;
    this.removeMessageListener();
    await this.loadEditorToken();
    if (this.editorUrl) {
      this.editorOpen = true;
      // Move overlay to body to escape any ancestor stacking context
      // (e.g. PrimeNG dialog transform animation traps fixed z-index)
      setTimeout(() => {
        const overlay = document.querySelector('.doc-editor-overlay') as HTMLElement | null;
        if (overlay && overlay.parentElement !== document.body) {
          document.body.appendChild(overlay);
        }
      });
    }
  }

  closeEditor(): void {
    this.editorOpen = false;
    this.editorUrl = null;
    this.removeMessageListener();
  }

  private async loadEditorToken(): Promise<void> {
    this.editorLoading.set(true);
    try {
      const currentContent = this.formGroup.get('content')?.value ?? '';
      const { url } = this.entity?.id
        ? await this.service.generateEditorToken(this.entity.id)
        : await this.service.generateEditorTokenNew(currentContent);
      this.editorUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.registerMessageListener();
    } catch {
      this.toastService.show({ color: 'error', message: 'Erro ao carregar o editor' });
    } finally {
      this.editorLoading.set(false);
    }
  }

  private registerMessageListener(): void {
    this.removeMessageListener();
    this.messageListener = (event: MessageEvent) => {
      const { type, content } = event.data ?? {};
      if (type === 'doc-editor-saved') {
        this.formGroup.get('content')?.setValue(content);
        this.closeEditor();
      } else if (type === 'doc-editor-content-change') {
        this.formGroup.get('content')?.setValue(content);
      }
    };
    window.addEventListener('message', this.messageListener);
  }

  private removeMessageListener(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
  }

  private generateSlugLocal(title: string): string {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 100);
  }

  async save(): Promise<void> {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) return;

    const body: any = {
      ...this.entity,
      ...this.formGroup.value,
      path_assignments: this.selectedPaths(),
    };

    if (body.ai_tags && typeof body.ai_tags === 'string') {
      try {
        body.ai_tags = JSON.parse(body.ai_tags);
      } catch {}
    }

    this.loading.set(true);

    await this.service
      .save(body)
      .then((response: any) => {
        const saved = response?.data || response;
        if (saved?.id || saved) {
          this.toastService.show({ color: 'success', message: 'Salvo com sucesso!' });
          this.onSave.emit(saved);
          this.modalVisible = false;
        }
      })
      .catch(() => {
        this.toastService.show({
          color: 'error',
          message: 'Houve um erro ao salvar os dados do formulário!',
        });
      })
      .finally(() => {
        this.loading.set(false);
      });
  }

  ngOnDestroy(): void {
    this.removeMessageListener();
    this.titleSlugSub?.unsubscribe();
  }
}
